import React from 'react';
import { AlertTriangle, Check, Loader2, Smartphone, Wrench, X } from 'lucide-react';
import { STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, STOCK_PANEL } from './stockV2Ui';

const issueLabel = (code) => {
  switch (code) {
    case 'imei_missing': return 'IMEI manquant';
    case 'imei_invalid': return 'IMEI invalide';
    case 'categorie_suggested': return 'Catégorie suggérée';
    case 'designation_missing': return 'Désignation';
    case 'reference_missing': return 'Référence';
    case 'prix_vente_missing': return 'Prix vente';
    default: return 'Attention';
  }
};

const StockProductReviewModal = ({
  open,
  title = 'Vérification avant enregistrement',
  subtitle,
  lines = [],
  analyzing = false,
  saving = false,
  onClose,
  onConfirm,
  onLineChange,
}) => {
  if (!open) return null;

  const canConfirm = lines.length > 0 && lines.every((ln) => ln.can_save !== false) && !analyzing;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm" data-testid="stock-review-modal">
      <div className={`${STOCK_PANEL} w-full sm:max-w-3xl max-h-[92vh] flex flex-col shadow-2xl`}>
        <div className="p-4 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-white">{title}</h2>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            <p className="text-xs text-gray-400 mt-2">
              Analyse IA + règles métier — téléphones = IMEI obligatoire · écrans/batteries/écouteurs = pas d&apos;IMEI.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 text-gray-500 hover:text-white" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {analyzing && (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-400 text-sm">
              <Loader2 className="w-5 h-5 animate-spin text-red-400" />
              Analyse en cours…
            </div>
          )}

          {!analyzing && lines.map((ln) => (
            <div
              key={ln.index}
              className={`rounded-xl border p-3 space-y-3 ${ln.can_save === false ? 'border-amber-500/40 bg-amber-950/10' : 'border-white/10 bg-black/20'}`}
              data-testid={`review-line-${ln.index}`}
            >
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="min-w-0">
                  <p className="font-mono text-xs text-gray-500">{ln.reference || '—'}</p>
                  <p className="text-sm font-semibold text-white truncate">{ln.designation || 'Sans désignation'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {ln.needs_imei ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-950/40 text-red-300 text-[10px] font-bold uppercase border border-red-500/30">
                      <Smartphone className="w-3 h-3" /> Téléphone
                    </span>
                  ) : ln.is_piece ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-950/40 text-blue-300 text-[10px] font-bold uppercase border border-blue-500/30">
                      <Wrench className="w-3 h-3" /> Pièce
                    </span>
                  ) : (
                    <span className="text-[10px] text-gray-500 uppercase">Autre</span>
                  )}
                  {ln.confidence === 'ai' && (
                    <span className="text-[10px] text-purple-400">IA</span>
                  )}
                </div>
              </div>

              {ln.ai_reason && (
                <p className="text-[11px] text-purple-300/90 bg-purple-950/20 rounded px-2 py-1">{ln.ai_reason}</p>
              )}

              {(ln.issues || []).length > 0 && (
                <ul className="space-y-1">
                  {ln.issues.map((iss, ii) => (
                    <li key={ii} className="flex items-start gap-1.5 text-xs text-amber-200">
                      <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
                      <span><strong>{issueLabel(iss.code)}:</strong> {iss.message}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Désignation</label>
                  <input
                    type="text"
                    value={ln.designation || ''}
                    onChange={(e) => onLineChange(ln.index, { designation: e.target.value })}
                    className="input-dark w-full py-2 text-sm mt-0.5"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 uppercase">Catégorie</label>
                  <div className="flex gap-1 mt-0.5">
                    <input
                      type="text"
                      value={ln.categorie || ''}
                      onChange={(e) => onLineChange(ln.index, { categorie: e.target.value })}
                      placeholder={ln.suggested_categorie || 'Pièces, Écrans…'}
                      className="input-dark w-full py-2 text-sm"
                    />
                    {ln.suggested_categorie && ln.suggested_categorie !== ln.categorie && (
                      <button
                        type="button"
                        onClick={() => onLineChange(ln.index, { categorie: ln.suggested_categorie })}
                        className="shrink-0 px-2 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 text-[10px] hover:bg-emerald-950/30"
                        title="Appliquer suggestion"
                      >
                        {ln.suggested_categorie}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {ln.needs_imei && (
                <div className="space-y-1.5 pt-1 border-t border-white/5">
                  <p className="text-[10px] font-bold text-red-400 uppercase">IMEI * ({ln.quantite || 1} appareil(s))</p>
                  {(ln.imei_list || ['']).map((imeiVal, ii) => (
                    <input
                      key={ii}
                      type="text"
                      value={imeiVal}
                      onChange={(e) => {
                        const next = [...(ln.imei_list || [])];
                        next[ii] = e.target.value.replace(/\s/g, '');
                        onLineChange(ln.index, { imei_list: next });
                      }}
                      placeholder={`IMEI appareil ${ii + 1}`}
                      className="input-dark w-full py-2 text-sm font-mono"
                      data-testid={`review-imei-${ln.index}-${ii}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-white/10 flex flex-wrap gap-2 justify-end">
          <button type="button" onClick={onClose} className={STOCK_BTN_SECONDARY} disabled={saving}>
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm || saving}
            className={STOCK_BTN_PRIMARY}
            data-testid="stock-review-confirm"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? 'Enregistrement…' : 'Confirmer l\'enregistrement'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StockProductReviewModal;
